<script setup lang="ts">
import { ref,reactive,onMounted,onBeforeUnmount,toRefs,watch } from 'vue'
import CustomList from "../../../../compositions/CustomList.vue";
import ModalDialog from "../../../../compositions/ModalDialog.vue";
import SearchBox from "../../../../compositions/SearchBox.vue";
import SelectBox from "../../../../compositions/SelectBox.vue";
import Chart from "../../../../compositions/Chart.vue";
import SummaryMedicineComponent from '../../../Common/SummaryMedicineComponent.vue';
import config from "../../../../config.js";
import { errorAlert } from "../../../../plugins/alert";
import { getFieldValue } from "../../../../plugins/utility";
import qs from 'qs';
import axios from '../../../../plugins/axios.ts';
import { deepCopy,getDistinctColors } from "../../../../plugins/utility";
import HelpAction from '../../../../compositions/HelpAction.vue';

const props = defineProps({
    apis:{
      type:Object,
      required:true,
    },
    identityId: {
      typ:Number,
      default:() => {
        return 0;
      }
    },
    searchParams: {
      typ:Object,
      default:() => {
        return {};
      }
    },
    isVisible:{
        type:Boolean,
        default: () => {
            return false;
        }
    }
})

const { isVisible,identityId,searchParams } = toRefs(props);
const listRef = ref();
const searchParamsData = reactive(props.searchParams);
const medicineSummaryFields = reactive([
  {field_name: '薬局名', field_key: 'pharmacy_name', width: '10%', align: 'text-left',sorted:true},
  {field_name: '卸業者名', field_key: 'wholesaler_name', width: '10%', align: 'text-left',sorted:true},
  {field_name: '伝票番号', field_key: 'slip_number', width: '10%', align: 'text-right',sorted:true},
  {field_name: '採用品', field_key: 'product_detail_name', width: '20%', align: 'text-left',sorted:true},
  {field_name: '薬価', field_key: 'unit_price_org', width: '10%', align: 'text-right',sorted:true},
  {field_name: '購入額', field_key: 'unit_price', width: '10%', align: 'text-right',sorted:true},
  {field_name: '薬価差率(%)', field_key: 'price_org_rate', width: '10%', align: 'text-right',sorted:true},
  {field_name: '数量', field_key: 'number', width: '10%', align: 'text-center',sorted:true},
  {field_name: 'ロット数', field_key: 'lot_number', width: '10%', align: 'text-center',sorted:true},
]);
const summaryData = reactive([]);
const summaryChartData = reactive([]);
const summaryChartHistoryData = reactive([]);
const onSummaryHistoryTypeChange = (value,label) => {
  summaryHistoryType.value = value;
  getSummaryHistory();
}
const getSummary = async () => {
  let params = deepCopy(searchParamsData);
  const paramsSerializer = (params) => qs.stringify(params,{ arrayFormat: 'repeat'});
  let response = await axios.get(props.apis.summary_stock_wholesaler_summary,{params:params,paramsSerializer});
  summaryData.splice(0);
  summaryData.push(response.data.summary);
}
const getSummaryHistory = async () => {
  let params = deepCopy(searchParamsData);
  const paramsSerializer = (params) => qs.stringify(params,{ arrayFormat: 'repeat'});
  let response = await axios.get(props.apis.summary_stock_wholesaler_summary_list,{params:params,paramsSerializer});
  //　バー
  let chartRecord = {};
  chartRecord.labels = [];
  for (let i=0;i<response.data.list.length;i++) {
    chartRecord.labels.push(response.data.list[i].summary_key);
  }
  chartRecord.datasets = [];
  let stock_price = [];
  for (let i=0;i<response.data.list.length;i++) {
    stock_price.push(response.data.list[i].stock_price);
  }
  chartRecord.datasets.push({
      type:'bar',
      label:'取引額',
      backgroundColor:'#2dc181',
      data:stock_price,
  });

  summaryChartHistoryData.splice(0);
  summaryChartHistoryData.push(chartRecord);

  // 円グラフ
  let chartPieRecord = {};
  chartPieRecord.labels = [];
  for (let i=0;i<response.data.list.length;i++) {
    chartPieRecord.labels.push(response.data.list[i].summary_key_short);
  }
  let colors = getDistinctColors(chartPieRecord.labels.length);
  let pieData = [];
  for (let i=0;i<response.data.list.length;i++) {
    pieData.push(response.data.list[i].stock_price);
  }

  let allPrice = 0;
  for (let i=0;i<pieData.length;i++) {
    allPrice += pieData[i];
  }
  for (let i=0;i<pieData.length;i++) {
    pieData[i] = Math.round((pieData[i] / allPrice)*100);
  }
  chartPieRecord.datasets = [
    {
      type:'pie',
      label:'卸別割合',
      backgroundColor:colors,
      data:pieData,
      datalabels:{display:true}
    }
  ];
  summaryChartData.splice(0);
  summaryChartData.push(chartPieRecord);
}

const refreshView = (force=false) => {
  if (isVisible.value == false) {
    return;
  }
  if (typeof listRef.value !== 'undefined') {
    listRef.value.resetItem();
    getSummary();
    getSummaryHistory();
  }
}
watch(isVisible,(newValue, oldValue) => {
  if (newValue) {
    refreshView();
  }
})
watch(identityId,(newValue, oldValue) => {
  refreshView();
})
watch(searchParams,(newValue, oldValue) => {
  //条件に薬局が追加されたら列を追加する
  let newPharmacyIds = oldValue['pharmacy_ids'];
  if (newPharmacyIds.length == 1 && newPharmacyIds[0] == 0) {
    newPharmacyIds = [];
  }
  refreshView();
},{deep:true})
onMounted(() => {
  refreshView();
});
</script>

<style scoped>
.summary-header {
  min-height:54px;
}
.summary-chart {
  min-height:342px;
}
</style>
<template>
<div v-show="isVisible" class="relative">
  <div class="absolute top-2 left-2" style="z-index:39;">
    <help-action
      help-key="dashboard_wholesaler_help"
    ></help-action>
  </div>
  <div class="pl-8 w-full border flex items-center justify-between px-4 summary-header" v-for="(summaryRecord,index) in summaryData" :key="index">
    <div class="text-center">
      <div><span class="border-b text-sm">卸取引金額(税込み)</span></div>
      <div class="text-bold text-lg">\{{ summaryRecord.stock_price.toLocaleString() }}</div>
    </div>
  </div>
  <div class="w-full mt-1 flex flex-row summary-chart">
    <div class="w-1/5" v-for="(summaryChartRecord,index) in summaryChartData" :key="index">
      <div class="font-bold">【卸別割合】</div>
        <chart
          chart-type="pie"
          height="280px"
          :chart-data="summaryChartRecord"
          :data-labels="{
            formatter: function(value, context) {
                return '';
            },
            display:false,
            font: {
              weight: 'bold',
              size: 16,
            }
          }"
        ></chart>
    </div>
    <div class="w-4/5" v-for="(summaryChartHistoryRecord,index) in summaryChartHistoryData" :key="index">
      <div class="font-bold">【卸別集計】</div>
      <!--
      <div class="w-1/5">
        <select-box
          :value="summaryHistoryType"
          :options="summaryHistoryOptions"
          :on-change="onSummaryHistoryTypeChange"
        ></select-box>
      </div>
      -->
      <div>
        <chart
          chart-type="bar"
          height="280px"
          :chart-data="summaryChartHistoryRecord"
          :data-labels="{
            formatter: function(value, context) {
                return (value ? value.toLocaleString() : '');
            },
            display:true,
            font: {
              weight: 'bold',
              size: 16,
            }
          }"
        ></chart>
      </div>
    </div>
  </div>
  <div class="w-full mt-1">
    <div class="font-bold">【卸取引一覧】</div>
    <custom-list
      ref="listRef"
      :api-url="apis.summary_stock_wholesaler_list"
      :table-fields="medicineSummaryFields"
      :option-fields="searchParamsData"
      :is-downloadable="true"
      font-size="0.8em"
      height="40vh"
    >
    <template v-slot:item="slotProps">
      <summary-medicine-component
        :apis="apis"
        :item="slotProps.item"
        :fields="medicineSummaryFields"
      ></summary-medicine-component>
    </template>
    </custom-list>
  </div>
</div>
</template>
